import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { SignalCard } from "@/components/SignalCard";
import { CtaBand } from "@/components/CtaBand";
import { site } from "@/config/site";
import { platformSystems } from "@/features/work/catalog";
import { work } from "@/lib/content/load";
import { headingText } from "@/lib/heading";

const systemProducts = [
  "ecosystem.Ai",
  "EcosystemCode",
  "Agentic Campaigning",
] as const;

const productLinks: Record<
  (typeof systemProducts)[number],
  { href: string; label: string }
> = {
  "ecosystem.Ai": { href: "https://ecosystem.ai", label: "ecosystem.ai" },
  EcosystemCode: {
    href: "https://ecosystemcode.com",
    label: "ecosystemcode.com",
  },
  "Agentic Campaigning": {
    href: "https://ecosystem.ai/agentic-campaigns",
    label: "Agentic Campaigns",
  },
};

const featuredOrder = [
  "ecosystem-ai",
  "ecosystemcode",
  "network-analysis",
  "open-source",
];

export function WorkPage() {
  const featuredWork = [...work]
    .filter((item) => item.meta.featured)
    .sort(
      (a, b) =>
        featuredOrder.indexOf(a.meta.slug) - featuredOrder.indexOf(b.meta.slug),
    );
  return (
    <>
      <Seo
        title="Projects"
        description="ecosystem.Ai, EcosystemCode, network analysis, public engineering, and earlier projects including Innovation for Jobs."
        path="/work"
      />
      <section className="wrap section" style={{ paddingTop: "4.5rem" }}>
        <p className="eyebrow">Projects</p>
        <h1>Platforms that have to keep running</h1>
        <p className="lede">
          Three current products under {site.legalEntity}. ecosystem.Ai puts behavioral
          prediction into production. EcosystemCode turns a validated architecture into a
          running system. Agentic Campaigning plans, governs, and measures campaigns on
          that same platform.
        </p>
      </section>
      <section className="wrap grid-2">
        {featuredWork.map((item) => (
            <SignalCard
              key={item.meta.slug}
              title={item.meta.title}
              to={item.meta.canonicalPath}
              eyebrow={item.meta.themes[0]?.replace(/-/g, " ")}
            >
              <p>{item.meta.summary}</p>
            </SignalCard>
          ))}
      </section>
      <section className="wrap section">
        <p className="section-kicker">Systems</p>
        <h2>Three products</h2>
        <p className="lede">
          A description of what each part is for. A link appears where the work is
          public.
        </p>
        {systemProducts.map((product) => (
          <div key={product} className="section">
            <h3>
              {productLinks[product] ? (
                <a href={productLinks[product].href}>{headingText(product)}</a>
              ) : (
                headingText(product)
              )}
            </h3>
            <div className="grid-2">
              {platformSystems
                .filter((system) => system.product === product)
                .map((system) => (
                  <article className="card" key={system.id} id={system.id}>
                    <h3>{headingText(system.title)}</h3>
                    <p>{system.purpose}</p>
                    {"links" in system ? (
                      <p>
                        {system.links.map((link, index) => (
                          <span key={link.href}>
                            {index > 0 ? " · " : null}
                            <a href={link.href}>{link.label}</a>
                          </span>
                        ))}
                      </p>
                    ) : null}
                  </article>
                ))}
            </div>
          </div>
        ))}
      </section>
      <section className="wrap section">
        <p className="section-kicker">Open engineering</p>
        <p>
          Public code lives mainly under{" "}
          <a href={site.profiles.githubOrg}>github.com/ecogenetic</a>.{" "}
          <Link to="/work/open-source">See the public code</Link>.
          The path from a prediction story to a running system is on{" "}
          <Link to="/building">Building</Link>.
        </p>
        <p>
          <a href={site.profiles.gpts}>Custom GPTs</a>
        </p>
      </section>
      <section className="wrap section">
        <p className="section-kicker">Earlier projects</p>
        <div className="grid-2">
          <SignalCard
            eyebrow="2016"
            title="ecosystem.Ai, before the pivot"
            to="/work/ecosystem-pre-pivot"
          >
            <p>
              Ecosystem graphs, company timelines, and a prediction server. The
              current platform is the case above.
            </p>
          </SignalCard>
          <SignalCard
            eyebrow="2016"
            title="Innovation for Jobs"
            to="/work/i4j"
          >
            <p>
              Analytics co-chair. The summit deck maps categories, organizations,
              investors, people, and places in the jobs ecosystem.
            </p>
          </SignalCard>
        </div>
      </section>
      <CtaBand title="Bring a product or architecture question" />
    </>
  );
}
